import { PasswordInputWithValidation } from 'acscp/components/common/validation/passwordInputWithValidation';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import { FetchRequest, FetchResetStatus } from 'acscp/actions';
import { CountriesRequest, RequestClass } from 'acscp/api/requests';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { AppState } from 'acscp/store';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { Form } from 'acscp/components/common/form';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import React from 'react';
import formStyle from 'acscp/components/common/form.module.scss';
import loginPageFormStyle from './loginPageForms.module.scss';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import { PasswordStrengthAssessment } from '../common/passwordStrengthAssessment';
import { AcceptTAndCs } from '../common/acceptTAndCs';
import { CreateAccountRequest } from 'acscp/api/requests/createAccountRequest';
import { LocaleCode } from 'acscp/translations';
import { Country } from 'acscp/api/models/country';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { CountryAndStateOfResidenceInputs } from 'acscp/components/auth/countryAndStateOfResidenceInputs';
import { StateValidationChecks } from 'acscp/components/common/helpers/stateValidationChecks';
import { CreateAccountFormSuccess } from './createAccountFormSuccess';
import { isApplicationRunningOniOS } from '../common/helpers/isApplicationRunningOniOS';
import { TelephoneNumberInput } from 'acscp/components/auth/telephoneNumberInput';

const messages = defineMessages({
    firstNameError: {
        id: 'web.components.auth.createAccountForm.firstNameInvalid',
        defaultMessage: 'First name is required.'
    },
    surnameError: {
        id: 'web.components.auth.createAccountForm.surnameInvalid',
        defaultMessage: 'Surname is required.'
    },
    countryOfResidenceError: {
        id: 'web.components.auth.createAccountForm.countryOfResidenceInvalid',
        defaultMessage: 'Country of residence is required.'
    },
    stateOfResidenceError: {
        id: 'web.components.auth.createAccountForm.stateOfResidenceInvalid',
        defaultMessage: 'State is required for the selected country.'
    },
    provinceOfResidenceError: {
        id: 'web.components.auth.createAccountForm.provinceOfResidenceInvalid',
        defaultMessage: 'Province is required for the selected country.'
    },
    emailError: {
        id: 'web.components.auth.createAccountForm.emailInvalid',
        defaultMessage: 'Please enter a valid email.'
    },
    missingCountryCallingCode: {
        id: 'web.components.auth.createAccountForm.missingCountryCallingCode',
        defaultMessage: 'Please select a country code.'
    },
    passwordError: {
        id: 'web.components.auth.createAccountForm.passwordRequired',
        defaultMessage: 'Password is required.'
    },
    confirmPasswordError: {
        id: 'web.components.auth.createAccountForm.passwordsShouldMatch',
        defaultMessage: 'Passwords should match.'
    },
    firstNamePlaceholder: {
        id: 'web.components.auth.createAccountForm.firstNamePlaceholder',
        defaultMessage: 'FIRST NAME'
    },
    surnamePlaceholder: {
        id: 'web.components.auth.createAccountForm.surnamePlaceholder',
        defaultMessage: 'SURNAME'
    },
    emailPlaceholder: {
        id: 'web.components.auth.createAccountForm.emailPlaceholder',
        defaultMessage: 'EMAIL ADDRESS'
    },
    passwordPlaceholder: {
        id: 'web.components.auth.createAccountForm.passwordPlaceholder',
        defaultMessage: 'PASSWORD'
    },
    confirmPasswordPlaceholder: {
        id: 'web.components.auth.createAccountForm.confirmPasswordPlaceholder',
        defaultMessage: 'CONFIRM PASSWORD'
    },
});

type CreateAccountFormOwnProps = {
    redirectToAfterSignUp?: string
} & InjectedIntlProps;

interface CreateAccountFormStateProps {
    countries: Country[];
    countriesRequestState: RequestStateProps;
    accountRequestState: RequestStateProps;
    locale: LocaleCode;
    confirmationEmailSenderAddress: string;
}

interface CreateAccountFormDispatchProps {
    fetchCountries: () => void;
    createAccount: (firstName: string, surname: string,
                    countryOfResidence: string, stateOfResidence: string,
                    username: string, email: string,
                    callingCodeCountry: string, telephoneNumber: string,
                    password: string,
                    locale: LocaleCode) => void;
    resetCreateAccountErrors: () => void;
}

interface CreateAccountForm {
    firstName: string;
    surname: string;
    countryOfResidence?: Country;
    stateOfResidenceId: string;
    username: string;
    emailAddress: string;
    callingCodeCountry?: Country;
    telephoneNumber: string;
    password: string;
    confirmPassword: string;
    acceptTAndCs: boolean;
}
type CreateAccountFormInternalState = CreateAccountForm & {
    hasStartedInitialCountriesFetch: boolean;
    showPasswordAssessment: boolean;
    passwordStrongEnough: boolean;
    showErrors: boolean;
    showStateOfResidence: boolean;
    callingCodeCountryHasBeenSelectedByUser: boolean;
};

type CreateAccountFormProps = CreateAccountFormOwnProps & CreateAccountFormStateProps & CreateAccountFormDispatchProps;

export class CreateAccountFormPresentational extends React.Component<CreateAccountFormProps, CreateAccountFormInternalState> {
    private validator: FormValidator;

    constructor(props: CreateAccountFormProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        this.state = {
            hasStartedInitialCountriesFetch: false,
            firstName: '',
            surname: '',
            stateOfResidenceId: '',
            username: '',
            emailAddress: '',
            callingCodeCountry: undefined,
            telephoneNumber: '',
            password: '',
            confirmPassword: '',
            showPasswordAssessment: false,
            passwordStrongEnough: false,
            acceptTAndCs: false,
            showErrors: false,
            showStateOfResidence: false,
            callingCodeCountryHasBeenSelectedByUser: false,
        };
    }

    private getValidationRules(): ValidationRule[] {
        const translator = this.props.intl.formatMessage;

        return [
            {
                validatedPropertyName: 'firstName',
                validationCheck: StringValidationChecks.isRequired(() => this.state.firstName),
                errorMessage: translator(messages.firstNameError)
            },
            {
                validatedPropertyName: 'surname',
                validationCheck: StringValidationChecks.isRequired(() => this.state.surname),
                errorMessage: translator(messages.surnameError)
            },
            {
                validatedPropertyName: 'countryOfResidence',
                validationCheck: StringValidationChecks.isRequired(
                    () => this.state.countryOfResidence && this.state.countryOfResidence.id || null),
                errorMessage: translator(messages.countryOfResidenceError)
            },
            {
                validatedPropertyName: 'stateOfResidence',
                validationCheck: StateValidationChecks.isStateSelectionValidForCountry(
                    () => this.state.stateOfResidenceId,
                    () => this.state.countryOfResidence),
                    errorMessage:
                    () => this.state.countryOfResidence && this.state.countryOfResidence.stateType === 'STATE'
                        ? translator(messages.stateOfResidenceError)
                        : translator(messages.provinceOfResidenceError)
            },
            {
                validatedPropertyName: 'username',
                validationCheck: StringValidationChecks.isEmail(() => this.state.username),
                errorMessage: translator(messages.emailError)
            },
            {
                validatedPropertyName: 'callingCodeCountry',
                validationCheck: () => !this.state.telephoneNumber || !!this.state.callingCodeCountry,
                errorMessage: translator(messages.missingCountryCallingCode)
            },
            {
                validatedPropertyName: 'password',
                validationCheck: StringValidationChecks.isRequired(() => this.state.password),
                errorMessage: translator(messages.passwordError)
            },
            {
                validatedPropertyName: 'confirmPassword',
                validationCheck: StringValidationChecks.stringMatch(() => this.state.confirmPassword, () => this.state.password),
                errorMessage: translator(messages.confirmPasswordError)
            }
        ];
    }

    fetchCountries = () => {
        this.props.fetchCountries();

        // hasStartedInitialCountriesFetch indicates to the render function that the state of the countries fetch request can be relied upon
        this.setState({hasStartedInitialCountriesFetch: true});
    }

    componentDidMount(): void {
        this.props.resetCreateAccountErrors();

        // Delay the countries fetch due to an unidentified request / render bug on the iOS app (IBOS-1965). 4+ days spent trying to debug
        // Might be fine now that IBOS-1950 is done; check if RequestLoyaltySchemeAccountForm works reliably
        if (isApplicationRunningOniOS()) {
            setTimeout(() => this.fetchCountries(), 200);
        } else {
            this.fetchCountries();
        }
    }

    firstNameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ firstName: event.target.value })

    surnameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ surname: event.target.value })

    countryOrStateChanged = (country: Country | undefined, stateId: string) => {
        const newCallingCodeCountry =
            !this.state.callingCodeCountryHasBeenSelectedByUser && !!country && !!country.callingCode
                ? country
                : this.state.callingCodeCountry;

        this.setState({
            countryOfResidence: country,
            stateOfResidenceId: stateId,
            callingCodeCountry: newCallingCodeCountry,
        });
    }

    usernameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ username: event.target.value })

    emailAddressChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ emailAddress: event.target.value })

    telephoneNumberChanged = (country: Country | undefined, telephoneNumber: string) =>
        this.setState({
            callingCodeCountryHasBeenSelectedByUser: true,
            callingCodeCountry: country,
            telephoneNumber,
        })

    passwordFieldFocused = (event: React.ChangeEvent) =>
        this.setState({ showPasswordAssessment: true })

    passwordFieldsBlurred = (event: React.ChangeEvent) =>
        this.setState({ showPasswordAssessment: false })

    passwordChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ password: event.target.value })

    confirmPasswordChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ confirmPassword: event.target.value })

    isStateValid = () => {
        return this.validator.isStateValid() &&
            this.state.acceptTAndCs && this.state.passwordStrongEnough;
    }

    createAccount = () => {
        this.props.resetCreateAccountErrors();

        if (!this.props.accountRequestState.isLoading) {
            const {
                firstName, surname,
                countryOfResidence, stateOfResidenceId,
                username, emailAddress,
                callingCodeCountry, telephoneNumber,
                password, confirmPassword } = this.state;

            if (this.isStateValid()) {
                const countryId = countryOfResidence && countryOfResidence.id || '';
                const callingCodeCountryId = callingCodeCountry && callingCodeCountry.id || '';
                this.props.createAccount(
                    firstName, surname,
                    countryId, stateOfResidenceId,
                    username, emailAddress,
                    callingCodeCountryId, telephoneNumber,
                    password,
                    this.props.locale);
            }
        }

        this.setState({ showErrors: true });
    }

    passwordStrength = (strong: boolean) => {
        this.setState({ passwordStrongEnough: strong });
    }

    toggleTermsAndConditionsCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        return this.setState({ acceptTAndCs: event.target.checked });
    }

    render(): JSX.Element {
        if (this.props.accountRequestState.success) {
            return <CreateAccountFormSuccess confirmationEmailSenderAddress={this.props.confirmationEmailSenderAddress} />;
        }
        return (
            <Form
                submit={this.createAccount}
                header={
                    !USE_MOBILE_APP_LOGIN_PAGE
                        ? <FormattedMessage id={'web.components.auth.createAccountForm.header'} defaultMessage={'CREATE AN ACCOUNT'} />
                        : <div />
                }
                formClassName={loginPageFormStyle.LoginPageForm}
            >
                {USE_MOBILE_APP_LOGIN_PAGE && <Link
                    to={'/login'}
                    className={classNames(formStyle.TextLink, formStyle.TextLinkLeftAlign)}
                >
                    <FormattedMessage
                        id={'mobile.components.pages.createAccountForm.back'}
                        defaultMessage={'< Back'}
                    />
                </Link>}
                {this.renderFormBody()}
            </Form>
        );
    }

    private renderFormBody(): JSX.Element {
        if (this.state.hasStartedInitialCountriesFetch
            && !this.props.countriesRequestState.isLoading
            && !this.props.countriesRequestState.success) {
            return <RequestErrorMessage requestState={this.props.countriesRequestState} />;
        }

        const { firstName, surname, username, emailAddress, password, confirmPassword } = this.state;
        const { isLoading, requestError } = this.props.accountRequestState;
        const countriesListLoading = this.props.countriesRequestState.isLoading;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        return (
            <>
                {!USE_MOBILE_APP_LOGIN_PAGE && <FormattedMessage
                    id={'web.components.auth.createAccountForm.description'}
                    defaultMessage={
                        'If you don\'t have an account we highly recommend creating one ' +
                        'so you can take advantage of all its great features, including viewing your documents offline when using the app.'}
                />}
                <InputWithValidation
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={firstName}
                    placeholder={this.props.intl.formatMessage(messages.firstNamePlaceholder)}
                    onChange={this.firstNameChanged}
                    validationErrors={validationErrors}
                    autoFocus={true}
                />
                <InputWithValidation
                    type="text"
                    name="surname"
                    id="surname"
                    value={surname}
                    placeholder={this.props.intl.formatMessage(messages.surnamePlaceholder)}
                    onChange={this.surnameChanged}
                    validationErrors={validationErrors}
                />
                <CountryAndStateOfResidenceInputs
                    countryInputName="countryOfResidence"
                    stateInputName="stateOfResidence"
                    validationErrors={validationErrors}
                    optionsAreLoading={countriesListLoading}
                    countryOptions={this.props.countries}
                    namingLocale={this.props.locale}
                    selectedCountry={this.state.countryOfResidence}
                    selectedStateId={this.state.stateOfResidenceId}
                    onChange={this.countryOrStateChanged}
                />
                <InputWithValidation
                    type="email"
                    name="username"
                    id="username"
                    value={username}
                    placeholder={this.props.intl.formatMessage(messages.emailPlaceholder)}
                    onChange={this.usernameChanged}
                    validationErrors={validationErrors}
                />
                <InputWithValidation
                    type="email"
                    name="emailAddress"
                    id="emailAddress"
                    value={emailAddress}
                    placeholder={this.props.intl.formatMessage(messages.emailPlaceholder)}
                    onChange={this.emailAddressChanged}
                    validationErrors={validationErrors}
                    autoComplete="doNotAutoComplete"
                    tabIndex={-1}
                    inputContainerClassNames={loginPageFormStyle._EmailFieldContainer}
                />
                <TelephoneNumberInput
                    callingCodeCountryInputName="callingCodeCountry"
                    telephoneNumberInputName="telephoneNumber"
                    validationErrors={validationErrors}
                    optionsAreLoading={countriesListLoading}
                    countryOptions={this.props.countries}
                    namingLocale={this.props.locale}
                    selectedCallingCodeCountry={this.state.callingCodeCountry}
                    telephoneNumber={this.state.telephoneNumber}
                    onChange={this.telephoneNumberChanged}
                />
                {/* Create a hidden username field immediately before the password field (mimicking the login form) to
                 workaround the behaviour of the Firefox password manager - see https://bugzilla.mozilla.org/show_bug.cgi?id=499223. */}
                <input
                    type="email"
                    name="hiddenUsername"
                    id="hiddenUsername"
                    value={username}
                    formNoValidate={true}
                    style={{display: 'none'}}
                />
                <div
                    className={classNames(
                        loginPageFormStyle.PasswordInputs,
                        { [loginPageFormStyle.PasswordInputsMobileApp]: USE_MOBILE_APP_LOGIN_PAGE }
                    )}
                    onFocus={this.passwordFieldFocused}
                    onBlur={this.passwordFieldsBlurred}
                >
                    <div>
                        <PasswordInputWithValidation
                            name="password"
                            id="password"
                            placeholder={this.props.intl.formatMessage(messages.passwordPlaceholder)}
                            value={password}
                            onChange={this.passwordChanged}
                            validationErrors={validationErrors}
                            autoComplete="new-password"
                        />
                        {USE_MOBILE_APP_LOGIN_PAGE && this.state.showPasswordAssessment && <PasswordStrengthAssessment
                            password={password}
                            passwordStrengthCallback={this.passwordStrength}
                            intl={this.props.intl}
                        />}
                    </div>
                    <PasswordInputWithValidation
                        name="confirmPassword"
                        id="confirmPassword"
                        placeholder={this.props.intl.formatMessage(messages.confirmPasswordPlaceholder)}
                        value={confirmPassword}
                        onChange={this.confirmPasswordChanged}
                        validationErrors={validationErrors}
                        autoComplete="new-password"
                    />
                </div>
                {!USE_MOBILE_APP_LOGIN_PAGE && this.state.showPasswordAssessment && <PasswordStrengthAssessment
                    password={password}
                    passwordStrengthCallback={this.passwordStrength}
                    intl={this.props.intl}
                />}
                <AcceptTAndCs
                    toggleTAndCsFunction={this.toggleTermsAndConditionsCheckbox}
                    showErrors={this.state.showErrors}
                    borderedBox={true}
                    acceptTAndCsIsChecked={this.state.acceptTAndCs}
                />
                <RequestErrorMessage requestState={this.props.accountRequestState} />
                <ButtonWithLoadingIndicator isLoading={isLoading} disabled={countriesListLoading}>
                    <FormattedMessage
                        id={'web.components.auth.createAccountForm.createAccountButton'}
                        defaultMessage={'CREATE AN ACCOUNT'}
                    />
                </ButtonWithLoadingIndicator>
            </>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): CreateAccountFormStateProps {
    return {
        countries: state.countries.countries,
        countriesRequestState: getRequestError(CountriesRequest),
        accountRequestState: getRequestError(CreateAccountRequest),
        locale: state.intl.locale,
        confirmationEmailSenderAddress: state.createAccountResponse.confirmationEmailSenderAddress
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: CreateAccountFormProps): CreateAccountFormDispatchProps {
    return {
        fetchCountries: () => dispatch(new FetchRequest(new CountriesRequest())),
        createAccount: (firstName: string, surname: string,
                        countryOfResidence: string, stateOfResidence: string,
                        username: string, emailAddress: string,
                        callingCodeCountry: string, telephoneNumber: string,
                        password: string,
                        locale: LocaleCode) =>
            dispatch(new FetchRequest(new CreateAccountRequest(
                firstName, surname,
                countryOfResidence, stateOfResidence,
                username, emailAddress,
                callingCodeCountry, telephoneNumber,
                password,
                locale,
                ownProps.redirectToAfterSignUp))),
        resetCreateAccountErrors: () => dispatch(new FetchResetStatus(CreateAccountRequest))
    };
}

export const CreateAccountForm = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (injectIntl(CreateAccountFormPresentational));
